<template>
  <b-modal
    v-model="open"
    centered
    size="lg"
    :ok-title="confirmLabel"
    cancel-title="CANCEL"
    :ok-disabled="loading || !!loadError || busy"
    :no-close-on-backdrop="busy"
    :no-close-on-esc="busy"
    data-test="share-files-modal"
    @show="onShow"
    @ok.prevent="onConfirm"
  >
    <template #modal-title>
      <h3 class="font-weight-bold">Choose Files To Share</h3>
    </template>

    <p class="share-files__lead mb-3">
      <strong>{{ projectName || 'This project' }}</strong
      ><br />
      Only the files you tick are shared with this company. Files added to the
      project later are not shared automatically.
    </p>

    <b-alert v-if="loadError" show variant="danger" data-test="load-error">
      {{ loadError }}
      <a href="#" class="alert-link ml-1" @click.prevent="load">Try again</a>
    </b-alert>

    <template v-else>
      <b-form-input
        v-model.trim="search"
        class="mb-3"
        placeholder="Search by name or tag"
        aria-label="Search files"
        data-test="share-files-search"
      />

      <div
        v-if="files.length"
        class="share-files__select-all d-flex align-items-center"
      >
        <b-form-checkbox
          :checked="allVisiblePicked"
          :indeterminate="someVisiblePicked && !allVisiblePicked"
          :disabled="!visibleFiles.length"
          data-test="select-all"
          @change="toggleAll"
        >
          Select all
        </b-form-checkbox>
        <span class="ml-auto share-files__count" data-test="selected-count">
          {{ pickedCount }} of {{ files.length }} selected
        </span>
      </div>

      <b-overlay :show="loading" rounded="sm" spinner-small>
        <div class="share-files__list">
          <div
            v-for="file in visibleFiles"
            :key="file.id"
            class="share-files__row"
            :data-test="`file-row-${file.id}`"
            @click="toggle(file.id, !isPicked(file.id))"
          >
            <b-form-checkbox
              :checked="isPicked(file.id)"
              class="share-files__check"
              @click.native.stop
              @change="(checked) => toggle(file.id, checked)"
            >
              <span class="sr-only">Share {{ file.name }}</span>
            </b-form-checkbox>
            <span class="share-files__main">
              <span class="share-files__name">{{ file.name }}</span>
              <span class="share-files__meta">
                {{ typeLabel(file) }}
                <template v-if="file.expires_at">
                  ·
                  <span :class="{ 'share-files__expired': isExpired(file) }">{{
                    expiryText(file)
                  }}</span>
                </template>
              </span>
              <span
                v-if="file.tags && file.tags.length"
                class="share-files__tags"
              >
                <span
                  v-for="tag in file.tags"
                  :key="tag"
                  class="share-files__tag"
                  >{{ tag }}</span
                >
              </span>
            </span>
          </div>

          <div
            v-if="!loading && !files.length"
            class="share-files__empty"
            data-test="no-files"
          >
            This project has no files to share yet.
          </div>
          <div
            v-else-if="!loading && !visibleFiles.length"
            class="share-files__empty"
          >
            No files match “{{ search }}”.
          </div>
        </div>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import dayjs from 'dayjs'
import Api from '@/api'
import { fileTypeLabel } from '@/utils/projectFiles'

const PER_PAGE = 100
// A project this large is far past what one share should hold; stop rather
// than page forever.
const MAX_PAGES = 20

/**
 * Pick which of a project's own documents a share includes. Only active files
 * are listed (the API never shares an archived one). The modal returns the
 * ticked ids on confirm; the caller decides what to do with them (send them
 * with a new share, or save them onto an existing one).
 */
export default {
  name: 'ProjectShareFilesModal',

  props: {
    value: { type: Boolean, default: false },
    projectId: { type: [Number, String], default: null },
    projectName: { type: String, default: '' },
    // Ids ticked when the modal opens.
    selectedIds: { type: Array, default: () => [] },
    confirmLabel: { type: String, default: 'SAVE' },
    // The caller is saving: keep the modal open and the button disabled.
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      files: [],
      picked: {},
      search: '',
      loading: false,
      loadError: '',
      loadedFor: null,
    }
  },

  computed: {
    open: {
      get() {
        return this.value
      },
      set(val) {
        if (!val && this.busy) return
        this.$emit('input', val)
      },
    },

    visibleFiles() {
      const term = this.search.toLowerCase()
      if (!term) return this.files
      return this.files.filter(
        (f) =>
          (f.name || '').toLowerCase().includes(term) ||
          (f.tags || []).some((t) => t.includes(term)),
      )
    },

    pickedCount() {
      return this.files.filter((f) => this.picked[f.id]).length
    },

    allVisiblePicked() {
      return (
        this.visibleFiles.length > 0 &&
        this.visibleFiles.every((f) => this.picked[f.id])
      )
    },

    someVisiblePicked() {
      return this.visibleFiles.some((f) => this.picked[f.id])
    },
  },

  methods: {
    async onShow() {
      this.search = ''
      const picked = {}
      this.selectedIds.forEach((id) => {
        picked[id] = true
      })
      this.picked = picked
      // Reload when the project changed, or the last load failed.
      if (this.loadedFor !== this.projectId || this.loadError) {
        await this.load()
      }
    },

    async load() {
      const projectId = this.projectId
      if (projectId == null) return
      this.loading = true
      this.loadError = ''
      try {
        const all = []
        let page = 1
        let lastPage = 1
        do {
          const { data } = await Api.get(`projects/${projectId}/files`, {
            params: { page, per_page: PER_PAGE },
          })
          all.push(...(data.data || []))
          lastPage = (data.meta && data.meta.last_page) || 1
          page++
        } while (page <= lastPage && page <= MAX_PAGES)

        if (projectId !== this.projectId) return
        this.files = all
        this.loadedFor = projectId
      } catch (error) {
        const body = error && error.response && error.response.data
        this.loadError =
          (body && body.message) ||
          'We couldn’t load this project’s files. Please try again.'
      } finally {
        this.loading = false
      }
    },

    isPicked(id) {
      return !!this.picked[id]
    },

    toggle(id, checked) {
      this.$set(this.picked, id, !!checked)
    },

    toggleAll(checked) {
      this.visibleFiles.forEach((f) => this.$set(this.picked, f.id, !!checked))
    },

    typeLabel(file) {
      return fileTypeLabel(file.mime_type, file.file_name)
    },

    isExpired(file) {
      return file.expiry_status === 'expired'
    },

    expiryText(file) {
      const date = dayjs(file.expires_at).format('DD/MM/YYYY')
      return this.isExpired(file) ? `Expired ${date}` : `Expires ${date}`
    },

    onConfirm() {
      // Only ids that are still listed: a file archived since the share was
      // made would be rejected by the API.
      const ids = this.files.filter((f) => this.picked[f.id]).map((f) => f.id)
      this.$emit('confirm', ids)
    },
  },
}
</script>

<style lang="scss" scoped>
.share-files__lead {
  color: #6b7280;
}

.share-files__lead strong {
  color: #111827;
}

.share-files__select-all {
  padding: 4px 4px 8px;
  border-bottom: 1px solid #e5e7eb;
}

.share-files__count {
  color: #6b7280;
  font-size: 0.875rem;
}

.share-files__list {
  min-height: 160px;
  max-height: 45vh;
  overflow-y: auto;
}

.share-files__row {
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding: 10px 4px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
}

.share-files__check {
  flex: 0 0 auto;
  margin-right: 8px;
}

.share-files__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.share-files__name {
  word-break: break-word;
}

.share-files__meta {
  color: #6b7280;
  font-size: 0.875rem;
}

.share-files__expired {
  color: #dc3545;
}

.share-files__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.share-files__tag {
  padding: 0 8px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
}

.share-files__empty {
  padding: 32px 0;
  text-align: center;
  color: #6c757d;
}
</style>
